/** A media element, as far as reading a file's length needs one. */
export type LengthReader = Pick<HTMLMediaElement, 'preload' | 'muted' | 'src' | 'duration' | 'currentTime' | 'addEventListener' | 'removeAttribute' | 'load'>

export const readerSource = {
  create: (kind: 'video' | 'audio'): LengthReader => document.createElement(kind),
}

/**
 * How long a video or audio file plays, in milliseconds, as this browser reads it before uploading. Null when it can't
 * tell in time: a codec it doesn't play (HEVC in Chrome on a computer, say) or a file it can't read. The worker measures
 * every clip anyway.
 */
export function readMediaDuration(file: Blob, kind: 'video' | 'audio', { timeoutMs = 10_000, signal }: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<number | null> {
  if (signal?.aborted || typeof URL.createObjectURL !== 'function') return Promise.resolve(null)
  return new Promise((resolve) => {
    const element = readerSource.create(kind)
    const url = URL.createObjectURL(file)
    let done = false
    const finish = (durationMs: number | null) => {
      if (done) return
      done = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
      // Let go of the file.
      element.removeAttribute('src')
      try {
        element.load()
      } catch {
        // nothing to stop
      }
      URL.revokeObjectURL(url)
      resolve(durationMs)
    }
    const cancel = () => finish(null)
    const known = () => {
      const seconds = element.duration
      if (Number.isFinite(seconds) && seconds > 0) finish(Math.round(seconds * 1000))
      return done
    }
    element.addEventListener('loadedmetadata', () => {
      if (known()) return
      // Some recordings (WebM from a browser, say) only say how long they are once read to the end: seeking past it does.
      if (element.duration === Infinity) element.currentTime = Number.MAX_SAFE_INTEGER
      else finish(null)
    })
    element.addEventListener('durationchange', known)
    element.addEventListener('error', () => finish(null))
    const timer = setTimeout(cancel, timeoutMs)
    signal?.addEventListener('abort', cancel)
    element.preload = 'metadata'
    element.muted = true
    element.src = url
  })
}
